import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Save, X } from "lucide-react";
import { atualizarUsuarioZenPass, criarUsuarioZenPass, listarUsuariosZenPass } from "../services/api";

const inputClass = `
  w-full px-3 py-2.5 rounded-xl border outline-none transition
  zen-card text-gray-900 placeholder:text-gray-400
  focus:ring-2 focus:ring-blue-500/60
  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
  dark:focus:ring-blue-400/40
`;
const labelClass = "block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1";

const vazio = {
  nome: "",
  email: "",
  senha: "",
  papel: "operador",
  ativo: true,
  modHistorico: false,
  modMensagens: false,
  modWhatsapp: false,
};

const UserModal = ({ show, onClose, mode, initial, onSave }) => {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(vazio);

  useEffect(() => {
    if (!show) return;
    setForm({
      nome: initial?.nome || "",
      email: initial?.email || "",
      senha: "",
      papel: initial?.papel || "operador",
      ativo: initial?.ativo !== false,
      modHistorico: !!initial?.modHistorico,
      modMensagens: !!initial?.modMensagens,
      modWhatsapp: !!initial?.modWhatsapp,
    });
  }, [show, initial]);

  if (!show) return null;

  const gestor = form.papel === "gestor";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return alert("Nome é obrigatório.");
    if (!form.email.trim()) return alert("E-mail é obrigatório.");
    if (!isEdit && form.senha.trim().length < 6) return alert("Senha deve ter no mínimo 6 caracteres.");
    if (isEdit && form.senha && form.senha.trim().length < 6) return alert("Senha deve ter no mínimo 6 caracteres.");
    await onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-xl rounded-2xl zen-card p-6 shadow-xl dark:bg-slate-950/70 dark:border-white/10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {isEdit ? "Editar usuário (ZenPass)" : "Novo usuário (ZenPass)"}
            </h3>
            <p className="text-sm text-gray-500 dark:text-slate-300/80">
              O usuário entra neste restaurante. Gestor acessa histórico, mensagens e WhatsApp.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10" type="button">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className={labelClass}>Nome</label>
            <input value={form.nome} onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))} className={inputClass} required />
          </div>
          <div>
            <label className={labelClass}>E-mail</label>
            <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className={inputClass} required />
          </div>
          <div>
            <label className={labelClass}>{isEdit ? "Nova senha (opcional)" : "Senha"}</label>
            <input
              type="password"
              value={form.senha}
              onChange={(e) => setForm((p) => ({ ...p, senha: e.target.value }))}
              className={inputClass}
              placeholder={isEdit ? "Deixe em branco para não alterar" : "Mínimo 6 caracteres"}
              required={!isEdit}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Papel</label>
              <select value={form.papel} onChange={(e) => setForm((p) => ({ ...p, papel: e.target.value }))} className={inputClass}>
                <option value="operador">Operador</option>
                <option value="gestor">Gestor</option>
              </select>
            </div>
            {isEdit && (
              <label className="flex items-end gap-2 pb-3 text-sm">
                <input type="checkbox" checked={form.ativo} onChange={(e) => setForm((p) => ({ ...p, ativo: e.target.checked }))} />
                Ativo
              </label>
            )}
          </div>
          {!gestor && (
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.modHistorico} onChange={(e) => setForm((p) => ({ ...p, modHistorico: e.target.checked }))} />
                Histórico
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.modMensagens} onChange={(e) => setForm((p) => ({ ...p, modMensagens: e.target.checked }))} />
                Mensagens
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.modWhatsapp} onChange={(e) => setForm((p) => ({ ...p, modWhatsapp: e.target.checked }))} />
                WhatsApp
              </label>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl bg-gray-100 text-sm dark:bg-white/10 dark:text-white">
              Cancelar
            </button>
            <button type="submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm">
              <Save size={16} />
              {isEdit ? "Salvar" : "Criar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ZenPassUsuarios = ({ token }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("new");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listarUsuariosZenPass(id, token);
      setUsuarios(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error(e);
      setUsuarios([]);
      alert("Erro ao listar usuários do ZenPass.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const onSave = async (form) => {
    const payload = {
      nome: form.nome.trim(),
      email: form.email.trim(),
      senha: form.senha,
      papel: form.papel,
      ativo: form.ativo,
      modHistorico: form.papel === "gestor" ? true : form.modHistorico,
      modMensagens: form.papel === "gestor" ? true : form.modMensagens,
      modWhatsapp: form.papel === "gestor" ? true : form.modWhatsapp,
    };
    try {
      if (modalMode === "new") await criarUsuarioZenPass(id, payload, token);
      else await atualizarUsuarioZenPass(id, selected.id, payload, token);
      setModalOpen(false);
      await load();
    } catch (e) {
      const data = e?.response?.data;
      const msg = typeof data === "string" ? data : data?.erro || data?.message || "Erro ao salvar usuário.";
      alert(msg);
    }
  };

  return (
    <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
      <div className="flex items-center justify-between gap-4">
        <button className="inline-flex items-center gap-2 text-sm" onClick={() => navigate("/licenciamento/zenpass/list")} type="button">
          <ArrowLeft size={18} /> Voltar
        </button>
        <button
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
          onClick={() => { setSelected(null); setModalMode("new"); setModalOpen(true); }}
          type="button"
        >
          <Plus size={18} /> Novo usuário
        </button>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-4">
        Usuários do ZenPass <span className="text-gray-500 dark:text-slate-400">(Licença #{id})</span>
      </h2>
      {loading ? (
        <div className="mt-4">Carregando...</div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="text-left py-3 px-4">Nome</th>
                <th className="text-left py-3 px-4">E-mail</th>
                <th className="text-left py-3 px-4">Papel</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-center py-3 px-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id} className="border-t dark:border-white/10">
                  <td className="py-3 px-4">{u.nome}</td>
                  <td className="py-3 px-4">{u.email}</td>
                  <td className="py-3 px-4">{u.papel === "gestor" ? "Gestor" : "Operador"}</td>
                  <td className="py-3 px-4">{u.ativo === false ? "Inativo" : "Ativo"}</td>
                  <td className="py-3 px-4 text-center">
                    <button
                      type="button"
                      className="p-2 rounded-full text-blue-600 hover:bg-blue-100 dark:text-blue-300"
                      title="Editar"
                      onClick={() => { setSelected(u); setModalMode("edit"); setModalOpen(true); }}
                    >
                      <Edit size={18} />
                    </button>
                  </td>
                </tr>
              ))}
              {!usuarios.length && (
                <tr><td colSpan={5} className="py-10 text-center text-gray-500">Nenhum usuário encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <UserModal
        show={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        initial={selected}
        onSave={onSave}
      />
    </div>
  );
};

export default ZenPassUsuarios;
